/**
 * Loaded values for one attribute on one record.
 *
 * Reads as inventory rather than schema: what is left, what it covers, and one
 * way to add more. Filters and the list appear once there is stock to filter,
 * not before.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, Hash, Upload } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Separator } from "@/shared/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  getValueStockRejections,
  toastApiError,
} from "@/shared/lib/api-error";
import { downloadLabelPdf, qrDataUrl } from "@/admin/lib/qr-label";
import {
  STOCK_FILE_MAX_BYTES,
  STOCK_RANGE_LIMIT,
  describeStockRange,
  parseStockValues,
} from "@/admin/lib/value-stock";
import {
  fetchAllValueStock,
  PRINT_ROW_LIMIT,
  useLoadValueStock,
  useRetireValueStock,
  useValueStock,
  type ValueStockEntry,
  type ValueStockRejection,
} from "@/admin/hooks/use-value-stock";

const PAGE_SIZE = 25;
/** Below this there is nothing worth filtering, so the tabs stay hidden. */
const FILTER_THRESHOLD = 10;

type Translate = ReturnType<typeof useTranslation>["t"];

function statusLabel(t: Translate, status: string): string {
  if (status === "available") return t("valueStock.statusAvailable", "Available");
  if (status === "in_use") return t("valueStock.statusUsed", "Used");
  if (status === "retired") return t("valueStock.statusRetired", "Retired");
  return status;
}

function rangeError(t: Translate, error: string): string {
  if (error === "shape")
    return t("valueStock.rangeErrorShape", "Both values must end with a number.");
  if (error === "prefix")
    return t(
      "valueStock.rangeErrorPrefix",
      "Both values must start with the same prefix.",
    );
  if (error === "order")
    return t(
      "valueStock.rangeErrorOrder",
      "The last value must come after the first.",
    );
  if (error === "limit")
    return t("valueStock.rangeErrorLimit", {
      defaultValue: "A range cannot cover more than {{limit}} values.",
      limit: STOCK_RANGE_LIMIT.toLocaleString(),
    });
  return error;
}

function rejectionReason(t: Translate, rejection: ValueStockRejection): string {
  if (rejection.reason === "in_use")
    return t("valueStock.rejectedInUse", "already used on a registration");
  if (rejection.reason === "other_scope")
    return t("valueStock.rejectedOtherScope", "already loaded elsewhere");
  return rejection.reason;
}

export function ValueStockCard({
  attribute,
  label,
  scopeEntity,
  scopeId,
  canEdit = true,
  qrEnabled = false,
}: {
  attribute: string;
  label: string;
  scopeEntity: string;
  scopeId: string;
  canEdit?: boolean;
  qrEnabled?: boolean;
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [rejections, setRejections] = useState<ValueStockRejection[]>([]);
  const [preview, setPreview] = useState<ValueStockEntry | null>(null);
  const [downloading, setDownloading] = useState(false);

  const scope = { entity: scopeEntity, id: scopeId };
  const { data, isPending } = useValueStock({
    attribute,
    scopeEntity,
    scopeId,
    status,
    query,
    page,
    pageSize: PAGE_SIZE,
  });
  const retire = useRetireValueStock();

  const counts = data?.counts;
  const total = counts?.total ?? 0;
  const results = data?.results;
  const rows = useMemo(() => results ?? [], [results]);

  const coverage = useMemo(() => {
    const first = data?.coverage?.first;
    const last = data?.coverage?.last;
    if (first && last && first !== last) return `${first} to ${last}`;
    return null;
  }, [data?.coverage?.first, data?.coverage?.last]);

  async function onRetire(id: string) {
    try {
      await retire.mutateAsync({ id, attribute, scope });
      toast.success(t("valueStock.retired", "Value retired."));
    } catch (err) {
      toastApiError(
        err,
        t("valueStock.retireFailed", "Could not retire that value."),
      );
    }
  }

  return (
    <section
      data-testid="value-stock-card"
      className="overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 bg-[var(--bg-subtle)] px-5 py-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] bg-[var(--green-bg)] text-[var(--green)]">
            <Hash className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ink-3)]">
              {label}
            </h2>
            {coverage && (
              <p className="truncate text-[12px] text-[var(--ink-3)]">{coverage}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {total > 0 && (
            <p className="text-[12px] text-[var(--ink-3)]">
              <span className="text-[var(--ink)]">{counts?.available ?? 0}</span>{" "}
              {t("valueStock.counts", {
                defaultValue: "available · {{used}} used · {{total}} total",
                used: counts?.in_use ?? 0,
                total,
              })}
            </p>
          )}
          {qrEnabled && canEdit && total > 0 && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="value-stock-print-page"
              disabled={downloading}
              onClick={() =>
                void downloadAll(
                  { attribute, scopeEntity, scopeId, status, query },
                  label,
                  setDownloading,
                  t,
                )
              }
            >
              {t("valueStock.printPage", "Download PDF")}
            </Button>
          )}
          {canEdit && (
            <Button
              size="sm"
              data-testid="value-stock-add-open"
              onClick={() => setAddOpen(true)}
            >
              {t("valueStock.addValues", "Add values")}
            </Button>
          )}
        </div>
      </div>

      <Separator className="h-px w-full bg-[var(--rule-strong)]" />

      <div className="p-5">
        {rejections.length > 0 && (
          <div
            data-testid="value-stock-rejections"
            className="mb-5 rounded-[8px] border-[0.5px] border-[var(--status-error)] bg-[var(--bg-subtle)] p-4"
          >
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-[12px] font-medium text-[var(--status-error)]">
                {t("valueStock.rejectedCount", {
                  defaultValue_one: "{{count}} value could not be added",
                  defaultValue_other: "{{count}} values could not be added",
                  count: rejections.length,
                })}
              </p>
              <Button variant="ghost" size="xs" onClick={() => setRejections([])}>
                {t("common.dismiss", "Dismiss")}
              </Button>
            </div>
            <ul className="flex flex-col gap-1">
              {rejections.map((rejection) => (
                <li key={rejection.value} className="text-[12px] text-[var(--ink-2)]">
                  <span className="font-mono">{rejection.value}</span>{" "}
                  {rejectionReason(t, rejection)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {isPending ? (
          <p className="text-[12px] text-[var(--ink-3)]">
            {t("common.loading", "Loading…")}
          </p>
        ) : total === 0 ? (
          <p className="text-[12px] text-[var(--ink-3)]">
            {t("valueStock.empty", "No values loaded yet.")}{" "}
            {canEdit &&
              t(
                "valueStock.emptyHint",
                "Add a range or paste a list to get started.",
              )}
          </p>
        ) : (
          <>
            {total >= FILTER_THRESHOLD && (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                {["", "available", "in_use", "retired"].map((value) => (
                  <Button
                    key={value || "all"}
                    variant={status === value ? "secondary" : "ghost"}
                    size="xs"
                    onClick={() => {
                      setStatus(value);
                      setPage(1);
                    }}
                  >
                    {value ? statusLabel(t, value) : t("common.all", "All")}
                  </Button>
                ))}
                <Input
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(1);
                  }}
                  placeholder={t("valueStock.searchPlaceholder", "Search values")}
                  className="ml-auto w-[200px]"
                />
              </div>
            )}

            <ul className="flex flex-col">
              {rows.map((row) => (
                <li
                  key={row.id}
                  className="flex items-center justify-between gap-3 border-b-[0.5px] border-b-[var(--rule)] py-2.5 last:border-b-0"
                >
                  {qrEnabled && row.scan_url ? (
                    <a
                      href={row.scan_url}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate font-mono text-[13px] text-[var(--ink)] underline decoration-[var(--rule-strong)] underline-offset-2 hover:text-[var(--green)]"
                    >
                      {row.value}
                    </a>
                  ) : (
                    <span className="truncate font-mono text-[13px] text-[var(--ink)]">
                      {row.value}
                    </span>
                  )}
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge variant={row.status === "available" ? "secondary" : "outline"}>
                      {statusLabel(t, row.status)}
                    </Badge>
                    {qrEnabled && row.scan_url && (
                      <Button
                        variant="ghost"
                        size="xs"
                        data-testid="value-stock-preview-qr"
                        onClick={() => setPreview(row)}
                      >
                        {t("valueStock.previewQr", "Preview")}
                      </Button>
                    )}
                    {canEdit && row.status === "available" && (
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => void onRetire(row.id)}
                      >
                        {t("valueStock.retire", "Retire")}
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {data && data.count > PAGE_SIZE && (
              <div className="mt-4 flex items-center justify-between gap-3">
                <p className="text-[12px] text-[var(--ink-3)]">
                  {(page - 1) * PAGE_SIZE + 1}-
                  {Math.min(page * PAGE_SIZE, data.count)} of {data.count}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="xs"
                    disabled={!data.previous}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    {t("common.previous", "Previous")}
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    disabled={!data.next}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    {t("common.next", "Next")}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <AddValuesDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        label={label}
        attribute={attribute}
        scope={scope}
        onRejected={setRejections}
      />
      {preview && (
        <QrPreviewDialog
          row={preview}
          label={label}
          canEdit={canEdit}
          onClose={() => setPreview(null)}
        />
      )}
    </section>
  );
}

function AddValuesDialog({
  open,
  onOpenChange,
  label,
  attribute,
  scope,
  onRejected,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  attribute: string;
  scope: { entity: string; id: string };
  onRejected: (rejections: ValueStockRejection[]) => void;
}) {
  const { t } = useTranslation();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [pasted, setPasted] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const load = useLoadValueStock();

  const range = start.trim() && end.trim() ? describeStockRange(start, end) : null;
  const pastedValues = useMemo(() => parseStockValues(pasted), [pasted]);

  function close() {
    setStart("");
    setEnd("");
    setPasted("");
    onOpenChange(false);
  }

  async function submit(payload: {
    values?: string[];
    range?: { start: string; end: string };
  }) {
    try {
      const result = await load.mutateAsync({ attribute, scope, ...payload });
      onRejected([]);
      toast.success(
        result.skipped > 0
          ? t("valueStock.addedWithSkipped", {
              defaultValue: "{{added}} added, {{skipped}} already loaded.",
              added: result.added,
              skipped: result.skipped,
            })
          : t("valueStock.added", {
              defaultValue_one: "{{count}} value added.",
              defaultValue_other: "{{count}} values added.",
              count: result.added,
            }),
      );
      close();
    } catch (err) {
      // A conflict is work the admin has to do, so it lands on the card and
      // stays there rather than disappearing with a toast. The payload sits
      // under the envelope's ``result``, same as every other coded 409.
      const rejected = getValueStockRejections(err);
      if (rejected) {
        onRejected(
          rejected.map((row) => ({
            value: row.value,
            reason: row.reason,
            used_by: row.usedBy,
            scope: row.scope,
          })),
        );
        close();
        return;
      }
      toastApiError(
        err,
        t("valueStock.addFailed", "Could not add those values."),
      );
    }
  }

  function onFile(file: File) {
    if (file.size > STOCK_FILE_MAX_BYTES) {
      toast.error(t("valueStock.fileTooLarge", "That file is too large."));
      return;
    }
    void file.text().then(setPasted);
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="w-full max-w-[520px]" bare showCloseButton={false}>
        <DialogHeader className="border-b-[0.5px] border-b-[var(--rule)] px-5 py-4">
          <DialogTitle className="text-xs font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
            {t("valueStock.addTo", {
              defaultValue: "Add to {{label}}",
              label,
            })}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="px-5 py-4">
          <Tabs defaultValue="range">
            <TabsList variant="line">
              <TabsTrigger value="range">
                {t("valueStock.tabRange", "Range")}
              </TabsTrigger>
              <TabsTrigger value="list">
                {t("valueStock.tabPaste", "Paste or upload")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="range" className="pt-4">
              <div className="flex gap-3">
                <div className="flex-1">
                  <Label htmlFor="stock-range-start">
                    {t("valueStock.firstValue", "First value")}
                  </Label>
                  <Input
                    id="stock-range-start"
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                    placeholder="e.g. UNIT-000001"
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor="stock-range-end">
                    {t("valueStock.lastValue", "Last value")}
                  </Label>
                  <Input
                    id="stock-range-end"
                    value={end}
                    onChange={(e) => setEnd(e.target.value)}
                    placeholder="e.g. UNIT-000100"
                  />
                </div>
              </div>
              <p className="mt-2 min-h-[18px] text-[12px] text-[var(--ink-3)]">
                {range && "error" in range
                  ? rangeError(t, range.error)
                  : range
                    ? t("valueStock.willBeAdded", {
                        defaultValue: "{{count}} values will be added.",
                        count: range.count,
                      })
                    : ""}
              </p>
              <Button
                className="mt-2"
                data-testid="value-stock-add-range"
                disabled={load.isPending}
                onClick={() => void submit({ range: { start, end } })}
              >
                {t("valueStock.addRange", "Add range")}
              </Button>
            </TabsContent>

            <TabsContent value="list" className="pt-4">
              <Label htmlFor="stock-paste">
                {t("valueStock.pasteLabel", "One per line, or comma separated")}
              </Label>
              <Textarea
                id="stock-paste"
                rows={6}
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                placeholder={"UNIT-000001\nUNIT-000002"}
              />
              <div className="mt-2 flex items-center justify-between gap-3">
                <p className="text-[12px] text-[var(--ink-3)]">
                  {pastedValues.length > 0
                    ? t("valueStock.willBeAdded", {
                        defaultValue: "{{count}} values will be added.",
                        count: pastedValues.length,
                      })
                    : ""}
                </p>
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                  <Upload className="h-3.5 w-3.5" />
                  {t("valueStock.uploadFile", "Upload file")}
                </Button>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.txt"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onFile(file);
                  e.target.value = "";
                }}
              />
              <Button
                className="mt-3"
                data-testid="value-stock-add-list"
                disabled={load.isPending}
                onClick={() => void submit({ values: pastedValues })}
              >
                {t("valueStock.addValues", "Add values")}
              </Button>
            </TabsContent>
          </Tabs>
        </DialogBody>
        <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] px-5 py-4">
          <Button variant="ghost" onClick={close}>
            {t("common.close", "Close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QrPreviewDialog({
  row,
  label,
  canEdit,
  onClose,
}: {
  row: ValueStockEntry;
  label: string;
  canEdit: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [src, setSrc] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!row.scan_url) return;
    void qrDataUrl(row.scan_url).then(setSrc);
  }, [row.scan_url]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-full max-w-[360px]" bare showCloseButton={false}>
        <DialogHeader className="border-b-[0.5px] border-b-[var(--rule)] px-5 py-4">
          <DialogTitle className="text-xs font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
            {t("valueStock.previewQrTitle", { defaultValue: "{{label}}", label })}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col items-center gap-3 px-5 py-6">
          {src ? (
            <img src={src} alt="" className="h-[200px] w-[200px]" />
          ) : (
            <p className="text-[12px] text-[var(--ink-3)]">
              {t("common.loading", "Loading…")}
            </p>
          )}
          <p className="font-mono text-[13px] text-[var(--ink)]">{row.value}</p>
          {row.scan_url && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                window.open(row.scan_url, "_blank", "noopener,noreferrer")
              }
            >
              <ExternalLink />
              {t("valueStock.openScanUrl", "Open product")}
            </Button>
          )}
        </DialogBody>
        <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] px-5 py-4">
          <Button variant="ghost" onClick={onClose}>
            {t("common.close", "Close")}
          </Button>
          {canEdit && (
            <Button
              data-testid="value-stock-print-one"
              disabled={busy}
              onClick={() => void downloadRows([row], label, setBusy, t)}
            >
              {t("valueStock.printOne", "Download PDF")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

async function downloadAll(
  scope: {
    attribute: string;
    scopeEntity: string;
    scopeId: string;
    status?: string;
    query?: string;
  },
  label: string,
  setBusy: (busy: boolean) => void,
  t: Translate,
) {
  setBusy(true);
  try {
    await downloadLabelPdf(
      (await fetchAllValueStock(scope)).flatMap((row) =>
        row.scan_url ? [{ value: row.value, scan_url: row.scan_url }] : [],
      ),
      label,
    );
  } catch (err) {
    if (err instanceof Error && err.name === "ValueStockPrintLimit") {
      toast.error(
        t("valueStock.downloadLimit", {
          defaultValue:
            "A PDF cannot include more than {{limit}} labels. Filter the list first.",
          limit: PRINT_ROW_LIMIT.toLocaleString(),
        }),
      );
      return;
    }
    toastApiError(err, t("valueStock.downloadFailed", "Could not download the labels."));
  } finally {
    setBusy(false);
  }
}

async function downloadRows(
  rows: ValueStockEntry[],
  label: string,
  setBusy: (busy: boolean) => void,
  t: Translate,
) {
  const printable = rows.flatMap((row) =>
    row.scan_url ? [{ value: row.value, scan_url: row.scan_url }] : [],
  );
  if (printable.length === 0) return;
  setBusy(true);
  try {
    await downloadLabelPdf(printable, label);
  } catch (err) {
    toastApiError(err, t("valueStock.downloadFailed", "Could not download the labels."));
  } finally {
    setBusy(false);
  }
}
